import {
  type ComponentPropsWithRef,
  forwardRef,
  type ReactNode,
  useId,
  useState,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import "./checkbox.scss";

interface Props extends Omit<ComponentPropsWithRef<"input">, "size"> {
  size?: "lg" | "md" | "sm" | "xs";
  label?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  (
    {
      size = "md",
      label,
      disabled,
      className,
      onMouseEnter,
      onMouseLeave,
      ...props
    },
    ref,
  ) => {
    const id = useId();
    const [isHovering, setIsHovering] = useState(false);

    return (
      <span
        className={clsx(
          "relative inline-flex items-center justify-center gap-2",
          {
            "pointer-events-none": disabled,
          },
        )}
      >
        {isHovering && (
          <span
            className={clsx(
              "absolute rounded-full bg-v2-cosmic/50 left-0 top-0 pointer-events-none",
              {
                "w-8 h-8 -ml-2 -mt-2": size === "md" || size === "lg",
                "w-7 h-7 -ml-[7px] -mt-[7px]": size === "sm",
                "w-6 h-6 -ml-[6px] -mt-[6px]": size === "xs",
              },
            )}
          ></span>
        )}
        <input
          ref={ref}
          type="checkbox"
          className={twMerge(
            "dasboard-v3-checkbox relative appearance-none font-awesome text-white font-black",
            "shrink-0 cursor-pointer rounded-sm border border-v2-gray/200 bg-v2-primary",
            "hover:border-v2-cosmic/400 hover:bg-v2-cosmic/50",
            "checked:border-v2-cosmic/400 checked:bg-v2-cosmic/400",
            "hover:checked:border-v2-cosmic/400 hover:checked:bg-v2-cosmic/400",
            "before:absolute before:w-[inherit] before:h-[inherit] before:top-[-1px]",
            "before:left-[-1px] before:text-xs",
            "before:flex before:items-center before:justify-center",
            clsx({
              "w-4 h-4": size === "md" || size === "lg",
              "w-3.5 h-3.5 before:text-2xs": size === "sm",
              "w-3 h-3 before:text-[9px] leading-[10px]": size === "xs",
              "border-v2-gray/50 checked:border-v2-gray/50 checked:bg-v2-gray/25":
                disabled,
            }),
            className,
          )}
          id={id}
          disabled={disabled}
          onMouseEnter={(event) => {
            setIsHovering(true);
            onMouseEnter?.(event);
          }}
          onMouseLeave={(event) => {
            setIsHovering(false);
            onMouseLeave?.(event);
          }}
          {...props}
        />
        {label && (
          <label
            htmlFor={id}
            className={clsx("m-0 cursor-pointer", {
              "text-base": size === "lg",
              "text-sm": size === "md",
              "text-xs": size === "sm",
              "text-2xs": size === "xs",
              "text-v2-gray/100": disabled,
            })}
            onMouseEnter={() => {
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setIsHovering(false);
            }}
          >
            {label}
          </label>
        )}
      </span>
    );
  },
);
